import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './AddToListButton.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

const AddToListButton = ({ 
  itemId, 
  itemType, // 'anime', 'manga', or 'character'
  totalEpisodes, // For anime: total episodes
  totalChapters, // For manga: total chapters
  totalVolumes,  // For manga: total volumes
  onStatusChange 
}) => {
  const { isLoggedIn, user } = useAuth();
  
  const [isInList, setIsInList] = useState(false);
  const [currentStatus, setCurrentStatus] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [showProgressPanel, setShowProgressPanel] = useState(false);
  const [showRatingPanel, setShowRatingPanel] = useState(false);
  const [showNotePanel, setShowNotePanel] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Progress tracking states
  const [currentEpisode, setCurrentEpisode] = useState(0);
  const [currentChapter, setCurrentChapter] = useState(0);
  const [currentVolume, setCurrentVolume] = useState(0);
  
  // Rating state
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  
  // Note state
  const [note, setNote] = useState('');
  
  // Ref for detecting clicks outside
  const optionsPanelRef = useRef(null);
  const progressPanelRef = useRef(null);
  const ratingPanelRef = useRef(null);
  const notePanelRef = useRef(null);

  // Format status for display with item type sensitivity
  const formatStatus = (status) => {
    // Base status mapping
    const statusMap = {
      'onHold': 'On Hold',
      'completed': 'Completed',
      'dropped': 'Dropped',
      'favorite': 'Favorite'
    };
    
    // Item-type specific mappings
    if (itemType === 'manga') {
      const mangaStatusMap = {
        'planning': 'Plan to Read',
        'reading': 'Reading'
      };
      return mangaStatusMap[status] || statusMap[status] || status;
    } else {
      const animeStatusMap = {
        'planning': 'Plan to Watch',
        'watching': 'Watching'
      };
      return animeStatusMap[status] || statusMap[status] || status;
    }
  };

  // Get status options based on item type
  const getStatusOptions = () => {
    if (itemType === 'character') {
      return ['favorite'];
    } else if (itemType === 'anime') {
      return ['planning', 'watching', 'completed', 'onHold', 'dropped'];
    } else if (itemType === 'manga') {
      return ['planning', 'reading', 'completed', 'onHold', 'dropped'];
    }
    return [];
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
  
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check if item is in list
  useEffect(() => {
    const checkStatus = async () => {
      if (!isLoggedIn || !user || !itemId) return;
      
      const token = localStorage.getItem('token');
      if (!token) return;
      
      try {
        setIsLoading(true);
        
        // Use the correct endpoint based on item type
        let endpoint;
        if (itemType === 'character') {
          // Use plural form for characters
          endpoint = `${API_BASE_URL}/api/user/characters/${itemId}`;
        } else {
          // Keep existing format for anime and manga
          endpoint = `${API_BASE_URL}/api/user/${itemType}/${itemId}`;
        }
        
        const response = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        console.log('CheckStatus full response:', response.data); // Add this for debugging
        
        if (response.data.success) {
          // Modify this part to match your new data structure
          const listItem = response.data.data;
          
          setIsInList(true);
          
          // For anime/manga
          if (itemType !== 'character') {
            // Use the status directly from the list item
            setCurrentStatus(listItem.status);
            
            // Set progress tracking states
            if (itemType === 'anime') {
              setCurrentEpisode(listItem.currentEpisode || 0);
            } else if (itemType === 'manga') {
              setCurrentChapter(listItem.currentChapter || 0);
              setCurrentVolume(listItem.currentVolume || 0);
            }
            
            // Set rating if exists
            if (listItem.rating !== undefined && listItem.rating !== null) {
              setRating(listItem.rating);
            }
            
            // Set note if exists
            if (listItem.notes) {
              setNote(listItem.notes);
            }
          } else {
            // For characters
            setCurrentStatus('favorite');
          }
        }
      } catch (err) {
        console.error('Error checking status:', err);
        if (err.response && err.response.status === 404) {
          setIsInList(false);
          setCurrentStatus('');
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    checkStatus();
  }, [isLoggedIn, user, itemId, itemType]);

  // Handle clicks outside panels
  useEffect(() => {
    function handleClickOutside(event) {
      if (optionsPanelRef.current && !optionsPanelRef.current.contains(event.target)) {
        setShowOptions(false);
      }
      if (progressPanelRef.current && !progressPanelRef.current.contains(event.target)) {
        setShowProgressPanel(false);
      }
      if (ratingPanelRef.current && !ratingPanelRef.current.contains(event.target)) {
        setShowRatingPanel(false);
      }
      if (notePanelRef.current && !notePanelRef.current.contains(event.target)) {
        setShowNotePanel(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleAddOrUpdate = async (status) => {
    if (!isLoggedIn) {
      window.location.href = '/login';
      return;
    }
    
    const token = localStorage.getItem('token');
    if (!token) {
      setError("Not logged in");
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Determine endpoint and method
      let baseEndpoint;
      if (itemType === 'character') {
        // Use plural form for characters
        baseEndpoint = `${API_BASE_URL}/api/user/characters`;
      } else {
        // Keep existing format for anime and manga
        baseEndpoint = `${API_BASE_URL}/api/user/${itemType}`;
      }
      
      const endpoint = isInList ? `${baseEndpoint}/${itemId}` : baseEndpoint;
      const method = isInList ? 'patch' : 'post';
      
      // Auto-set episode/chapter count to total when marking as completed
      let updatedEpisode = currentEpisode;
      let updatedChapter = currentChapter;
      let updatedVolume = currentVolume;
      
      // If status is 'completed', set progress to total
      if (status === 'completed') {
        if (itemType === 'anime' && totalEpisodes) {
          updatedEpisode = totalEpisodes;
          setCurrentEpisode(totalEpisodes);
        } else if (itemType === 'manga') {
          if (totalChapters) {
            updatedChapter = totalChapters;
            setCurrentChapter(totalChapters);
          }
          if (totalVolumes) {
            updatedVolume = totalVolumes;
            setCurrentVolume(totalVolumes);
          }
        }
      }
      
      // Prepare payload
      let payload = {};
      if (itemType === 'anime') {
        payload = isInList 
          ? { status, currentEpisode: updatedEpisode } 
          : { animeId: itemId, status, currentEpisode: status === 'completed' && totalEpisodes ? totalEpisodes : 0 };
      } else if (itemType === 'manga') {
        payload = isInList 
          ? { status, currentChapter: updatedChapter, currentVolume: updatedVolume } 
          : { 
              mangaId: itemId, 
              status, 
              currentChapter: status === 'completed' && totalChapters ? totalChapters : 0, 
              currentVolume: status === 'completed' && totalVolumes ? totalVolumes : 0 
            };
      } else {
        payload = { characterId: itemId, isFavorite: true };
      }

      console.log('Sending API request with payload:', payload);

      
      // Make request
      const response = await axios({
        method,
        url: endpoint,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        data: payload
      });

      console.log('API response:', response.data);
      
      if (response.data.success) {
        setIsInList(true);
        setCurrentStatus(status);
        if (onStatusChange) onStatusChange(status);
        
        // Open the rating panel if status is completed
        if (status === 'completed' && !isInList) {
          setShowOptions(false);
          setTimeout(() => {
            setShowRatingPanel(true);
          }, 300);
        } else if (status === 'completed' && isInList && rating === 0) {
          setShowOptions(false);
          setTimeout(() => {
            setShowRatingPanel(true);
          }, 300);
        }
        
        console.log(`Successfully ${isInList ? 'updated' : 'added'} to list with status: ${status}`);
      }
    } catch (err) {
      console.error("Error:", err);
      setError("Error processing request");
    } finally {
      setIsLoading(false);
      setShowOptions(false);
    }
  };

  const updateProgress = async () => {
    if (!isLoggedIn || !isInList) return;
    
    const token = localStorage.getItem('token');
    if (!token) return;

    console.log('Updating progress with:', {
      itemType,
      currentEpisode, // for anime
      currentChapter, // for manga
      currentVolume   // for manga
    });
    
    setIsLoading(true);
    
    try {
      // Determine endpoint based on item type
      let endpoint;
        if (itemType === 'character') {
          endpoint = `${API_BASE_URL}/api/user/characters/${itemId}`;
        } else {
          endpoint = `${API_BASE_URL}/api/user/${itemType}/${itemId}`;
    }
      
      // Prepare payload based on item type
      let payload = {};
      let shouldAutoComplete = false;
      let autoCompleteMessage = '';
      
      if (itemType === 'anime') {
        payload = { currentEpisode };
        
        // Check if auto-complete condition is met
        if (totalEpisodes && currentEpisode >= totalEpisodes) {
          payload.status = 'completed';
          shouldAutoComplete = true;
          autoCompleteMessage = `${totalEpisodes} episodes completed! Marked as completed.`;
        }
      } else if (itemType === 'manga') {
        payload = { currentChapter, currentVolume };
        
        // Check if auto-complete condition is met
        if (totalChapters && currentChapter >= totalChapters) {
          payload.status = 'completed';
          shouldAutoComplete = true;
          autoCompleteMessage = `${totalChapters} chapters completed! Marked as completed.`;
        }
        
        // Additional check for volume completion
        if (totalVolumes && currentVolume >= totalVolumes) {
          // If volumes are completed, ensure status is updated
          payload.status = 'completed';
          if (!shouldAutoComplete) {
            shouldAutoComplete = true;
            autoCompleteMessage = `${totalVolumes} volumes completed! Marked as completed.`;
          } else {
            autoCompleteMessage = `${totalChapters} chapters and ${totalVolumes} volumes completed! Marked as completed.`;
          }
        }
      }
      
      // Make request
      const response = await axios.patch(endpoint, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        if (payload.status) {
          setCurrentStatus(payload.status);
          if (onStatusChange) onStatusChange(payload.status);
        }
        
        if (shouldAutoComplete) {
          // Show a confirmation message or notification
          console.log(`Automatically marked as completed`);
          setSuccessMessage(autoCompleteMessage);
          
          // If auto-completed and rating is 0, show rating panel
          if (rating === 0) {
            setTimeout(() => {
              setShowRatingPanel(true);
            }, 300);
          }
          
          setTimeout(() => setSuccessMessage(""), 3000); // Clear after 3 seconds
        } else {
          setSuccessMessage("Progress updated successfully!");
          setTimeout(() => setSuccessMessage(""), 3000);
        }
        
        console.log(`Successfully updated progress`);
      }
    } catch (err) {
      console.error("Error updating progress:", err);
      setError("Error updating progress");
    } finally {
      setIsLoading(false);
      setShowProgressPanel(false);
    }
  };
  
  // Handle submitting a rating
  const submitRating = async () => {
    if (!isLoggedIn || !isInList || rating === 0) return;
    
    const token = localStorage.getItem('token');
    if (!token) return;
    
    setIsLoading(true);
    
    try {
      // Use correct endpoint based on item type
      let endpoint;
if (itemType === 'character') {
  endpoint = `${API_BASE_URL}/api/user/characters/${itemId}`;
} else {
  endpoint = `${API_BASE_URL}/api/user/${itemType}/${itemId}`;
}
      
      const payload = { rating };
      
      const response = await axios.patch(endpoint, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        setSuccessMessage(`Rating updated: ${rating}/10`);
        setTimeout(() => setSuccessMessage(""), 3000);
        console.log(`Successfully rated as ${rating}/10`);
      }
    } catch (err) {
      console.error("Error updating rating:", err);
      setError("Error updating rating");
    } finally {
      setIsLoading(false);
      setShowRatingPanel(false);
    }
  };
  
  // Handle submitting a note
  const submitNote = async () => {
    if (!isLoggedIn || !isInList) return;
    
    const token = localStorage.getItem('token');
    if (!token) return;
    
    setIsLoading(true);
    
    try {
      // Use correct endpoint based on item type
      let endpoint;
if (itemType === 'character') {
  endpoint = `${API_BASE_URL}/api/user/characters/${itemId}`;
} else {
  endpoint = `${API_BASE_URL}/api/user/${itemType}/${itemId}`;
}
      
      const payload = { notes: note };
      
      const response = await axios.patch(endpoint, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        setSuccessMessage("Note saved successfully!");
        setTimeout(() => setSuccessMessage(""), 3000);
        console.log(`Successfully saved note`);
      }
    } catch (err) {
      console.error("Error saving note:", err);
      setError("Error saving note");
    } finally {
      setIsLoading(false);
      setShowNotePanel(false);
    }
  };

  // Handle progress update for chapters/episodes
  const handleProgressUpdate = (increment = true) => {
    if (itemType === 'anime') {
      if (increment) {
        setCurrentEpisode(prev => {
          const next = prev + 1;
          console.log('Incrementing episode from', prev, 'to', next);
          // Auto-update to completed if reached total episodes
          if (totalEpisodes && next >= totalEpisodes) {
            return totalEpisodes;
          }
          return next;
        });
      } else if (currentEpisode > 0) {
        setCurrentEpisode(prev => prev - 1);
      }
    } else if (itemType === 'manga') {
      if (increment) {
        setCurrentChapter(prev => {
          const next = prev + 1;
          // Auto-update to completed if reached total chapters
          if (totalChapters && next >= totalChapters) {
            return totalChapters;
          }
          return next;
        });
      } else if (currentChapter > 0) {
        setCurrentChapter(prev => prev - 1);
      }
    }
  };

  // Handle volume update
  const handleVolumeUpdate = (increment = true) => {
    if (itemType === 'manga') {
      if (increment) {
        setCurrentVolume(prev => {
          const next = prev + 1;
          // Auto-update volumes if there is a total volume count
          if (totalVolumes && next >= totalVolumes) {
            return totalVolumes;
          }
          return next;
        });
      } else if (currentVolume > 0) {
        setCurrentVolume(prev => prev - 1);
      }
    }
  };

  const handleProgressChange = (e, type) => {
    const value = parseInt(e.target.value);
    if (isNaN(value) || value < 0) return;
    
    if (type === 'episode') {
      // Check if we're setting episode count to total
      const newValue = value;
      setCurrentEpisode(newValue);
      
      // If setting to max episodes, prepare to auto-complete
      if (totalEpisodes && newValue >= totalEpisodes) {
        setCurrentEpisode(totalEpisodes);
      }
    } else if (type === 'chapter') {
      // Check if we're setting chapter count to total
      const newValue = value;
      setCurrentChapter(newValue);
      
      // If setting to max chapters, prepare to auto-complete
      if (totalChapters && newValue >= totalChapters) {
        setCurrentChapter(totalChapters);
      }
    } else if (type === 'volume') {
      const newValue = value;
      setCurrentVolume(newValue);
      
      // If setting to max volumes, prepare for auto-complete
      if (totalVolumes && newValue >= totalVolumes) {
        setCurrentVolume(totalVolumes);
      }
    }
  };

  const handleRemove = async () => {
    if (!isLoggedIn || !isInList) return;
    
    const token = localStorage.getItem('token');
    if (!token) return;
    
    setIsLoading(true);
    
    try {
      // Use correct endpoint based on item type
      let endpoint;
if (itemType === 'character') {
  endpoint = `${API_BASE_URL}/api/user/characters/${itemId}`;
} else {
  endpoint = `${API_BASE_URL}/api/user/${itemType}/${itemId}`;
}
      
      const response = await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setIsInList(false);
        setCurrentStatus('');
        setCurrentEpisode(0);
        setCurrentChapter(0);
        setCurrentVolume(0);
        setRating(0);
        setNote('');
        if (onStatusChange) onStatusChange(null);
        console.log("Successfully removed from list");
      }
    } catch (err) {
      console.error("Error removing:", err);
      setError("Error removing from list");
    } finally {
      setIsLoading(false);
      setShowOptions(false);
    }
  };

  const handleProgressPanelToggle = () => {
    if (!isInList) return;
    
    if (itemType === 'character') return;
    
    setShowProgressPanel(!showProgressPanel);
    setShowOptions(false);
    setShowRatingPanel(false);
    setShowNotePanel(false);
  };
  
  const handleRatingPanelToggle = () => {
    if (!isInList || itemType === 'character') return;
    
    setShowRatingPanel(!showRatingPanel);
    setShowOptions(false);
    setShowProgressPanel(false);
    setShowNotePanel(false);
  };
  
  const handleNotePanelToggle = () => {
    if (!isInList || itemType === 'character') return;
    
    setShowNotePanel(!showNotePanel);
    setShowOptions(false);
    setShowProgressPanel(false);
    setShowRatingPanel(false);
  };

  // Function to check if the status is currently watching or reading
  const isWatchingOrReading = () => {
    return (
      (itemType === 'anime' && currentStatus === 'watching') || 
      (itemType === 'manga' && currentStatus === 'reading')
    );
  };

  if (!isLoggedIn) {
    return (
      <button 
        className="add-to-list-button login-prompt"
        onClick={() => window.location.href = '/login'}
      >
        Login to Add to List
      </button>
    );
  }

  if (isLoading) {
    return <div className="add-to-list-loading">Loading...</div>;
  }

  // Main button text
  const buttonText = isInList 
    ? formatStatus(currentStatus) 
    : `Add to ${itemType === 'character' ? 'Favorites' : 'My List'}`;

  // Progress text
  const progressText = () => {
    if (itemType === 'anime') {
      return `${currentEpisode}${totalEpisodes ? `/${totalEpisodes}` : ''} ep`;
    } else if (itemType === 'manga') {
      return `${currentChapter} ch, ${currentVolume} vol`;
    }
    return '';
  };

  return (
    <div className="add-to-list-container">
      {error && <div className="add-to-list-error">{error}</div>}
      {successMessage && <div className="add-to-list-success">{successMessage}</div>}
      
      <div className="button-group">
        <button 
          className={`add-to-list-button ${isInList ? 'in-list' : ''}`}
          onClick={() => {
            setShowOptions(!showOptions);
            setShowProgressPanel(false);
            setShowRatingPanel(false);
            setShowNotePanel(false);
          }}
        >
          {buttonText}
        </button>
        
        {isInList && itemType !== 'character' && (
          <>
            <button 
              className="progress-button"
              onClick={handleProgressPanelToggle}
            >
              {progressText()}
            </button>
            
            {/* Modified extra buttons section based on status */}
            {isWatchingOrReading() ? (
              <button 
                className="note-button" 
                style={{ width: '100%', gridColumn: '2 / 3' }}
                onClick={handleNotePanelToggle}
              >
                {note ? "Edit Note" : "Add Note"}
              </button>
            ) : (
              <div className="extra-buttons">
                {currentStatus === 'completed' && (
                  <button 
                    className="rating-button"
                    onClick={handleRatingPanelToggle}
                  >
                    {rating > 0 ? `★ ${rating}` : "Rate"}
                  </button>
                )}
                
                <button 
                  className="note-button"
                  onClick={handleNotePanelToggle}
                >
                  {note ? "Edit Note" : "Add Note"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
      
      {showOptions && (
        <div className="options-panel" ref={optionsPanelRef}>
          <div className="options-panel-title">
            {isInList ? 'Update status:' : 'Add to your list:'}
          </div>
          
          <div className="options-grid">
            {getStatusOptions().map(status => (
              <button 
                key={status}
                className={`option-btn ${currentStatus === status ? 'active' : ''}`}
                onClick={() => handleAddOrUpdate(status)}
              >
                {formatStatus(status)}
              </button>
            ))}
          </div>
          
          {isInList && (
            <button 
              className="remove-btn"
              onClick={handleRemove}
            >
              Remove from List
            </button>
          )}
          
          <button 
            className="cancel-btn"
            onClick={() => setShowOptions(false)}
          >
            Cancel
          </button>
        </div>
      )}
      
      {showProgressPanel && (
        <div className="progress-panel" ref={progressPanelRef}>
          <div className="progress-panel-title">
            Update progress
          </div>
          
          {itemType === 'anime' && (
            <div className="progress-control">
              <label>Episodes:</label>
              <div className="progress-input-group">
                <button 
                  className="progress-btn" 
                  onClick={() => handleProgressUpdate(false)}
                  disabled={currentEpisode <= 0}
                >
                  -
                </button>
                <input 
                  type="number"
                  min="0"
                  value={currentEpisode}
                  onChange={(e) => handleProgressChange(e, 'episode')}
                  className="progress-input"
                />
                <button 
                  className="progress-btn"
                  onClick={() => handleProgressUpdate(true)}
                >
                  +
                </button>
              </div>
              {totalEpisodes && <span className="total-indicator">/ {totalEpisodes}</span>}
            </div>
          )}
          
          {itemType === 'manga' && (
            <>
              <div className="progress-control">
                <label>Chapters:</label>
                <div className="progress-input-group">
                  <button 
                    className="progress-btn" 
                    onClick={() => handleProgressUpdate(false)}
                    disabled={currentChapter <= 0}
                  >
                    -
                  </button>
                  <input 
                    type="number"
                    min="0"
                    value={currentChapter}
                    onChange={(e) => handleProgressChange(e, 'chapter')}
                    className="progress-input"
                  />
                  <button 
                    className="progress-btn"
                    onClick={() => handleProgressUpdate(true)}
                  >
                    +
                  </button>
                </div>
                {totalChapters && <span className="total-indicator">/ {totalChapters}</span>}
              </div>
              
              <div className="progress-control">
                <label>Volumes:</label>
                <div className="progress-input-group">
                  <button 
                    className="progress-btn" 
                    onClick={() => handleVolumeUpdate(false)}
                    disabled={currentVolume <= 0}
                  >
                    -
                  </button>
                  <input 
                    type="number"
                    min="0"
                    value={currentVolume}
                    onChange={(e) => handleProgressChange(e, 'volume')}
                    className="progress-input"
                  />
                  <button 
                    className="progress-btn"
                    onClick={() => handleVolumeUpdate(true)}
                  >
                    +
                  </button>
                </div>
                {totalVolumes && <span className="total-indicator">/ {totalVolumes}</span>}
              </div>
            </>
          )}
          
          <div className="progress-buttons">
            <button 
              className="save-btn"
              onClick={updateProgress}
            >
              Save
            </button>
            <button 
              className="cancel-btn"
              onClick={() => setShowProgressPanel(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      
      {showRatingPanel && (
  <div className="rating-panel" ref={ratingPanelRef}>
    <div className="rating-panel-title">
      Rate this {itemType}
    </div>
    
    {!isMobile ? (
      // Desktop view with stars
      <div className="rating-stars">
        {[...Array(10)].map((_, index) => {
          const ratingValue = index + 1;
          return (
            <span
              key={ratingValue}
              className={`rating-star ${ratingValue <= (hoveredRating || rating) ? "filled" : ""}`}
              onClick={() => setRating(ratingValue)}
              onMouseEnter={() => setHoveredRating(ratingValue)}
              onMouseLeave={() => setHoveredRating(0)}
            >
              ★
            </span>
          );
        })}
      </div>
    ) : (
      // Mobile view with number input
      <div className="rating-number-input">
        <div className="progress-input-group">
          <button 
            className="progress-btn" 
            onClick={() => setRating(prev => Math.max(1, prev - 1))}
            disabled={rating <= 1}
          >
            -
          </button>
          <input 
            type="number"
            min="1"
            max="10"
            value={rating || ""}
            onChange={(e) => {
              const value = parseInt(e.target.value);
              if (!isNaN(value) && value >= 1 && value <= 10) {
                setRating(value);
              } else if (e.target.value === "") {
                setRating(0);
              }
            }}
            className="progress-input"
            placeholder="1-10"
          />
          <button 
            className="progress-btn"
            onClick={() => setRating(prev => Math.min(10, prev + 1))}
            disabled={rating >= 10}
          >
            +
          </button>
        </div>
      </div>
    )}
    
    <div className="rating-value">
      {rating > 0 ? `${rating}/10` : "Select a rating"}
    </div>
    
    <div className="rating-buttons">
      <button 
        className="save-btn"
        onClick={submitRating}
        disabled={rating === 0}
      >
        Save
      </button>
      <button 
        className="cancel-btn"
        onClick={() => setShowRatingPanel(false)}
      >
        Cancel
      </button>
    </div>
  </div>
)}
      
      {showNotePanel && (
        <div className="note-panel" ref={notePanelRef}>
          <div className="note-panel-title">
            {note ? 'Edit note' : 'Add a note'}
          </div>
          
          <textarea
            className="note-textarea"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add your personal notes about this title..."
            rows={5}
          />
          
          <div className="note-buttons">
            <button 
              className="save-btn"
              onClick={submitNote}
            >
              Save
            </button>
            <button 
              className="cancel-btn"
              onClick={() => setShowNotePanel(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddToListButton;